<template>
   <div ref="target" class="relative" @mouseenter="show" @mouseleave="scheduleHide">
      <slot />
      <Transition
         name="fade"
         enter-active-class="transition duration-200 ease-in-out"
         enter-from-class="opacity-0 translate-y-2"
         enter-to-class="opacity-100 translate-y-0"
         leave-active-class="transition duration-200 ease-in-out"
         leave-from-class="opacity-100 translate-y-0"
         leave-to-class="opacity-0 translate-y-2"
      >
         <p
            v-if="showTooltip && text"
            ref="tooltip"
            class="rounded-small __shadow-bottom border-light-grey p-s absolute z-10 min-w-[200px] max-w-[300px] border bg-white p-2"
            :class="[tooltipPosition, { 'pointer-events-auto select-text': interactive }]"
            :style="anchoredStyle"
         >
            {{ text }}
         </p>
      </Transition>
   </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

type Props = {
   text?: string
   placement?: 'top' | 'bottom' | 'left' | 'right'
   interactive?: boolean
   anchorToPointer?: boolean
}

const props = withDefaults(defineProps<Props>(), {
   placement: 'top',
   interactive: false,
   anchorToPointer: false,
})

const HIDE_DELAY_MS = 150
const VIEWPORT_MARGIN = 8

const target = ref<HTMLElement>()
const tooltip = ref<HTMLElement>()
const showTooltip = ref<boolean>(false)
const pointerX = ref<number>(0)

let hideTimer: ReturnType<typeof setTimeout> | undefined

const isHorizontallyAnchored = computed(
   () => props.anchorToPointer && (props.placement === 'top' || props.placement === 'bottom')
)

const anchoredStyle = computed(() => {
   if (!isHorizontallyAnchored.value || !target.value || !tooltip.value) return undefined

   const rect = target.value.getBoundingClientRect()
   const tooltipWidth = tooltip.value.offsetWidth || 200
   const maxLeft = window.innerWidth - tooltipWidth - VIEWPORT_MARGIN
   const left = Math.max(VIEWPORT_MARGIN, Math.min(pointerX.value, maxLeft))

   return { left: `${left - rect.left}px` }
})

const basePosition = computed(() => {
   if (!target.value || !tooltip.value)
      return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2'

   const rect = target.value.getBoundingClientRect()
   const tooltipWidth = tooltip.value.offsetWidth || 200
   const tooltipHeight = tooltip.value.offsetHeight || 40
   const viewportWidth = window.innerWidth
   const viewportHeight = window.innerHeight

   switch (props.placement) {
      case 'top':
         const wouldClipTop = rect.top - tooltipHeight < 20
         if (wouldClipTop) {
            return 'top-full left-1/2 transform -translate-x-1/2 mt-2'
         }
         return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2'

      case 'bottom':
         const wouldClipBottom = rect.bottom + tooltipHeight > viewportHeight - 20
         if (wouldClipBottom) {
            return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2'
         }
         return 'top-full left-1/2 transform -translate-x-1/2 mt-2'

      case 'left':
         const wouldClipLeft = rect.left - tooltipWidth < 20
         if (wouldClipLeft) {
            return 'left-full top-1/2 transform -translate-y-1/2 ml-2'
         }
         return 'right-full top-1/2 transform -translate-y-1/2 mr-2'

      case 'right':
         const wouldClipRight = rect.right + tooltipWidth > viewportWidth - 20
         if (wouldClipRight) {
            return 'right-full top-1/2 transform -translate-y-1/2 mr-2'
         }
         return 'left-full top-1/2 transform -translate-y-1/2 ml-2'

      default:
         return 'bottom-full left-1/2 transform -translate-x-1/2 mb-2'
   }
})

const tooltipPosition = computed(() =>
   isHorizontallyAnchored.value
      ? basePosition.value.replace('left-1/2 transform -translate-x-1/2', '').trim()
      : basePosition.value
)

const show = (event?: MouseEvent) => {
   clearTimeout(hideTimer)
   if (!showTooltip.value && event) pointerX.value = event.clientX
   showTooltip.value = true
}

const hide = () => {
   showTooltip.value = false
}

const scheduleHide = () => {
   clearTimeout(hideTimer)
   if (!props.interactive) {
      hide()
      return
   }
   hideTimer = setTimeout(hide, HIDE_DELAY_MS)
}

onBeforeUnmount(() => clearTimeout(hideTimer))

// Recalculate position when tooltip opens
watch(showTooltip, async (newValue) => {
   if (newValue) {
      await nextTick()
      tooltipPosition.value
   }
})
</script>
